import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  AlertTriangle, Building2, Check, CheckSquare, Download, EyeOff, FileUp, Filter, History,
  Layers, LayoutGrid, Map as MapIcon, MapPin, Pencil, Play, RefreshCcw, Replace, SquareDashed, Trash2, X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  useBundleSheetSets,
  useCreateSheetSet,
  useCreateSheetSetsPerParcel,
  useDeleteSheetSet,
  useGenerateSheetSet,
  useOverwriteSheetPdf,
  useParcels,
  useGenerateSheetSets,
  useRetrySheetSet,
  useRetrySheetSets,
  useRouteVersionParcelIds,
  useSheetSets,
  useUpdateSheetSet,
  useUploadSheetPdfs,
} from "../../hooks";
import type { FormatkaMode, FormatkaSheetSet, Parcel, PowerProject, RouteVersion } from "../../types";
import { FORMATKA_MODE_LABEL, FORMATKA_TYPE_LABEL } from "../../types";
import { apiErr, fmtDateTime, isOfficeSideParcel, isPrivateSideParcel } from "../../utils";
import { SectionCard } from "../ui";
import { OWNERSHIP_OPTIONS, findOwnershipOption, ownershipCodeColor } from "@/features/ze_tenders/utils";

// „Prywatna" / „urzędowa" = strona własności z KATEGORII UZGODNIENIA policzonej przez backend
// (utils.parcelOwnershipSide — lustro services.parcel_inclusion_kind, ta sama reguła co
// kreskowanie na mapie); bez kategorii zostaje heurystyka: właściciel-osoba fizyczna albo kod
// własności 7/15 (KIEG daje kod, zanim wróci wypis, więc prywatna nie trafia tymczasem do
// urzędowych). Grunt w użytkowaniu wieczystym prywatnego użytkownika (gmina / powiat jest
// właścicielem gruntu, ale zgodę trzeba mieć też od użytkownika) należy do OBU list: dostaje
// własny zestaw prywatny I wchodzi do zestawu urzędu-właściciela.
const isPrivateParcel = (p: Parcel): boolean => isPrivateSideParcel(p);
const isOfficeParcel = (p: Parcel): boolean => isOfficeSideParcel(p);

/** Właściciel(e) działki do jednej linijki listy: pierwsze nazwisko/nazwa + „+n" na
 *  współwłaścicieli (pełna lista zostaje w tooltipie). Nazwa urzędu jest zdenormalizowana
 *  na `full_name`, więc `office_name` służy tylko za zapasowe źródło. Brak właścicieli =
 *  wypis jeszcze nie wrócił — piszemy to wprost, bo puste pole wygląda jak błąd. */
function ownerSummary(p: Parcel): { label: string; title: string } {
  const names = (p.owners ?? [])
    .map((o) => (o.full_name || o.office_name || "").trim())
    .filter(Boolean);
  if (names.length === 0) return { label: "—", title: "Brak właścicieli — działka bez wypisu" };
  const rest = names.length - 1;
  return {
    label: rest > 0 ? `${names[0]} +${rest}` : names[0],
    title: `Właściciel${names.length > 1 ? "e" : ""}: ${names.join("; ")}`,
  };
}

/** Jednostka ewidencyjna działki — wyłącznie to, co realnie stoi w polu. Bez fallbacku
 *  z prefiksu TERYT (ten mają szablony pism), bo tutaj kawałek identyfikatora udawałby
 *  uzupełnioną jednostkę; brak danych ma być widoczny jako brak. */
const parcelJednostka = (p: Parcel): string => (p.jednostka_ewidencyjna ?? "").trim();

// Kolejność chipów kodu własności = kolejność ze słownika (ULDK, potem ręczne);
// kody spoza słownika idą na koniec, brak kodu — całkiem na końcu.
const OWNERSHIP_ORDER = new Map(OWNERSHIP_OPTIONS.map((o, i) => [o.value, i]));
const ownershipRank = (code: string) => (code ? (OWNERSHIP_ORDER.get(code) ?? 900) : 999);

interface Facet {
  value: string;
  label: string;
  count: number;
  color?: string;
}

/** Zliczenie wartości jednego filtra (kod własności / jednostka ewidencyjna) po działkach. */
function countFacet(parcels: Parcel[], key: (p: Parcel) => string): Map<string, number> {
  const counts = new Map<string, number>();
  parcels.forEach((p) => {
    const v = key(p);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  });
  return counts;
}

/** Rząd przełączanych chipów jednego filtra. Nic niezaznaczone = filtr nieaktywny. */
function FacetChips({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: Facet[];
  selected: Set<string>;
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="section-label shrink-0 pt-[3px] w-[8rem] whitespace-nowrap">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const on = selected.has(o.value);
          return (
            <button
              key={o.value || "__none__"}
              type="button"
              onClick={() => onToggle(o.value)}
              title={o.label}
              className={clsx(
                "inline-flex items-center gap-1 rounded border px-1.5 py-[2px] text-[10px] font-mono transition-colors",
                on
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/10"
                  : "border-[var(--color-border-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]",
              )}
            >
              {o.color && (
                <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: o.color }} />
              )}
              <span className="truncate max-w-[11rem]">{o.label}</span>
              <span className="text-[var(--color-text-muted)]">{o.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Opcje tworzenia zestawów wybrane w modalu działek (prywatne i urzędowe). */
interface PickerOpts {
  includeOrientation: boolean;
  // Tryb urzędowy: zaznaczenie podzielone po urzędach — po jednym zestawie na grupę.
  officeGroups?: OfficeGroup[];
}

/** Działki urzędowe jednego adresata: jeden urząd = jeden wniosek = jeden zestaw
 *  formatek (własna numeracja arkuszy i własny arkusz orientacyjny). `key` = id urzędu;
 *  pusty klucz zbiera działki bez przypisanego urzędu-właściciela. */
interface OfficeGroup {
  key: string;
  name: string;
  parcelIds: string[];
}

const NO_OFFICE_LABEL = "bez przypisanego urzędu";

/** Odmiana „zestawy/zestawów" po liczbie (2–4 → zestawy, reszta → zestawów). */
const pluralZestawy = (n: number) =>
  n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "zestawy" : "zestawów";

/** Zaznaczone działki urzędowe podzielone po urzędzie właściciela-instytucji (ta sama
 *  reguła, po której kreator wniosków zakłada po jednym wniosku na urząd). Działka
 *  współwłasna dwóch urzędów wchodzi do zestawu KAŻDEGO z nich — każdy dostaje własny
 *  wniosek i własną mapę. Działki bez urzędu-właściciela (wypis jeszcze nie wrócił)
 *  idą do jednej wspólnej grupy na końcu. */
function officeGroupsOf(parcels: Parcel[], sel: Set<string>): OfficeGroup[] {
  const groups = new Map<string, OfficeGroup>();
  for (const p of parcels) {
    if (!sel.has(p.id)) continue;
    const offices = new Map<string, string>();
    for (const o of p.owners ?? []) {
      if (o.kind === "company" && o.office) {
        offices.set(o.office, (o.office_name || o.full_name || "").trim() || "Urząd");
      }
    }
    if (offices.size === 0) offices.set("", NO_OFFICE_LABEL);
    for (const [key, name] of offices) {
      const g = groups.get(key) ?? { key, name, parcelIds: [] };
      g.parcelIds.push(p.id);
      groups.set(key, g);
    }
  }
  return [...groups.values()].sort(
    (a, b) => (a.key ? 0 : 1) - (b.key ? 0 : 1) || a.name.localeCompare(b.name, "pl"),
  );
}

// Visual identity per set type: an icon, a chip tint, and a left accent bar.
const MODE_BADGE: Record<FormatkaMode, { label: string; icon: LucideIcon; chip: string; accent: string }> = {
  parcel:        { label: "Działka",   icon: MapPin,       chip: "text-sky-700 bg-sky-50 border-sky-200",         accent: "bg-sky-400" },
  parcel_office: { label: "Urzędowe",  icon: Building2,    chip: "text-amber-700 bg-amber-50 border-amber-200",    accent: "bg-amber-400" },
  project:       { label: "Projekt",   icon: MapIcon,      chip: "text-emerald-700 bg-emerald-50 border-emerald-200", accent: "bg-emerald-400" },
  custom:        { label: "Własny",    icon: SquareDashed, chip: "text-slate-600 bg-slate-100 border-slate-200",  accent: "bg-slate-400" },
  bundle:        { label: "Paczka",    icon: Layers,       chip: "text-violet-700 bg-violet-50 border-violet-200", accent: "bg-violet-400" },
};

const STATUS_PILL: Record<FormatkaSheetSet["status"], { label: string; cls: string; dot?: boolean }> = {
  draft:      { label: "Szkic",       cls: "text-slate-500 bg-slate-100" },
  generating: { label: "Generowanie", cls: "text-amber-700 bg-amber-100", dot: true },
  success:    { label: "Gotowe",      cls: "text-emerald-700 bg-emerald-100" },
  failure:    { label: "Błąd",        cls: "text-red-700 bg-red-100" },
};

/** Zestaw z BŁĘDEM ROZPLANOWANIA: wsad per działka założył wiersz, ale na wskazanej
 *  wersji trasy działka nie ma naszych obiektów — zero ramek, powód w `error`. To coś
 *  innego niż błąd generowania ZIP-a (ten ma ramki i chip „Generuj ZIP"). */
const isLayoutFailure = (s: FormatkaSheetSet): boolean =>
  s.status === "failure" && s.frames.length === 0;

/** Zestaw z BŁĘDEM GENEROWANIA: ramki są, ale paczka ZIP nie powstała (zadanie padło,
 *  worker ubity, restart serwera). Ponowienie = ten sam POST generate co pierwszy raz. */
const isZipFailure = (s: FormatkaSheetSet): boolean =>
  s.status === "failure" && s.frames.length > 0;

/** „1 działka / 3 działki / 5 działek". */
const pluralDzialki = (n: number) =>
  n === 1 ? "1 działka" : `${n} ${n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "działki" : "działek"}`;

/** Small pill button used for the row's inline actions (Generuj / Wrzuć / Mapa …). */
const CHIP =
  "inline-flex items-center gap-1 rounded px-2 py-[3px] text-[11px] font-medium border transition-colors disabled:opacity-40";

/** MapTab right-column card: the connection's formatka sheet sets + the
 *  project/custom entry points ("Nowy zestaw…"). Per-parcel entry lives in the
 *  parcel drawer. */
export function FormatkiSectionCard({
  connection,
  selectedVersion,
}: {
  connection: PowerProject;
  selectedVersion: RouteVersion | undefined;
}) {
  const navigate = useNavigate();
  const { data: sheetSets, isLoading } = useSheetSets(connection.id);
  const create = useCreateSheetSet();
  const createPerParcel = useCreateSheetSetsPerParcel();
  const retryAll = useRetrySheetSets(connection.id);
  const regenerateAll = useGenerateSheetSets(connection.id);
  const remove = useDeleteSheetSet(connection.id);
  const bundle = useBundleSheetSets();
  const [error, setError] = useState<string | null>(null);
  // Wynik wsadu / ponowienia: ile zestawów powstało, które działki zostały bez arkusza.
  // „ok" = wszystko się udało (neutralny baner), „warn" = coś wymaga uwagi (czerwony).
  const [notice, setNotice] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
  // Zestawy z ostatniego tworzenia (dowolnego rodzaju) — oznaczone „nowy" na liście, bo
  // po utworzeniu zostajemy na karcie i rozplanowanie każdego otwiera się stąd.
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  // Wspólny finał każdego „Nowy — …": żadnego samoczynnego wejścia do edytora (do
  // 25.09.2026 otwierał się edytor PIERWSZEGO zestawu i przy kilku nie było wiadomo,
  // czyje rozplanowanie widać) — nowe wiersze dostają „nowy", edytor otwiera się z wiersza.
  const announceCreated = (sets: FormatkaSheetSet[], what: string) => {
    setFresh(new Set(sets.map((s) => s.id)));
    setNotice({
      tone: "ok",
      text:
        `${what} — ${sets.length === 1 ? "oznaczony" : "oznaczone"} „nowy” na liście niżej. ` +
        "Rozplanowanie arkuszy otworzysz przyciskiem „Rozplanowanie” albo nazwą w wierszu.",
    });
  };
  const creating = create.isPending || createPerParcel.isPending;
  // null = closed; "private" → one set PER parcel; "office" → one set PER office.
  const [pickIntent, setPickIntent] = useState<null | "private" | "office">(null);
  const [sel, setSel] = useState<Set<string>>(new Set());

  const toggleSel = (id: string) =>
    setSel((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Jeden zestaw na działkę: gdy tę samą działkę wygenerowano kilka razy, bierzemy
  // tylko NAJNOWSZĄ instancję (po created_at) — bez powielania działki w paczce.
  const newestParcelSets = useMemo(() => {
    const byParcel = new Map<string, FormatkaSheetSet>();
    for (const s of sheetSets ?? []) {
      if (s.mode !== "parcel") continue;
      // Wiersz z błędem rozplanowania nie ma arkuszy — nie ma czego łączyć w paczkę.
      if (isLayoutFailure(s)) continue;
      const key = s.parcel_label ?? s.id;
      const prev = byParcel.get(key);
      if (!prev || s.created_at > prev.created_at) byParcel.set(key, s);
    }
    return [...byParcel.values()];
  }, [sheetSets]);

  const allParcelsSelected =
    newestParcelSets.length > 0 && newestParcelSets.every((s) => sel.has(s.id));

  // Działki, dla których wsad nie znalazł arkusza — czerwone wiersze na liście,
  // do ponowienia (pojedynczo na wierszu albo wszystkie naraz) na wybranej wersji trasy.
  const layoutFailed = useMemo(() => (sheetSets ?? []).filter(isLayoutFailure), [sheetSets]);
  // Zestawy, którym nie powstała paczka ZIP — ponowienie to zwykłe generate, ale
  // zebrane w jeden przycisk, żeby po restarcie serwera nie klikać wiersz po wierszu.
  const zipFailed = useMemo(() => (sheetSets ?? []).filter(isZipFailure), [sheetSets]);

  const regenerateAllFailed = () => {
    if (zipFailed.length === 0) return;
    setError(null);
    setNotice(null);
    regenerateAll.mutate(zipFailed.map((s) => s.id), {
      onSuccess: (r) =>
        setNotice(
          r.failed === 0
            ? { tone: "ok", text: `Uruchomiono ponownie generowanie ${r.ok} ${r.ok === 1 ? "zestawu" : "zestawów"} — postęp widać w wierszach.` }
            : { tone: "warn", text: `Uruchomiono ponownie ${r.ok} z ${r.total}; ${r.failed} odrzucone: ${r.firstError}` },
        ),
      onError: (e) => setError(apiErr(e)),
    });
  };

  const retryAllFailed = () => {
    if (layoutFailed.length === 0) return;
    setError(null);
    setNotice(null);
    retryAll.mutate(
      { ids: layoutFailed.map((s) => s.id), routeVersion: selectedVersion?.id },
      {
        onSuccess: (r) =>
          setNotice(
            r.ok === r.total
              ? { tone: "ok", text: `Rozplanowano ponownie wszystkie ${r.total} działki — zestawy są znów szkicami.` }
              : {
                  tone: "warn",
                  text:
                    `Rozplanowano ponownie ${r.ok} z ${r.total}. Pozostałe działki nadal nie mają naszych` +
                    ` obiektów na wersji ${selectedVersion?.label ?? "trasy"} — wybierz inną wersję albo` +
                    " rozszerz ramkę zestawu działki sąsiedniej w edytorze.",
                },
          ),
        onError: (e) => setError(apiErr(e)),
      },
    );
  };

  // Zaznaczenie wszystkich działek jednym kliknięciem (najnowszy zestaw każdej),
  // gotowe do „Połącz w paczkę". Powtórne kliknięcie czyści zaznaczenie.
  const toggleAllParcels = () =>
    setSel(allParcelsSelected ? new Set() : new Set(newestParcelSets.map((s) => s.id)));

  // Combine the selected sets into ONE bundle set (mode=BUNDLE) that generates
  // its own ZIP and becomes a single upload/merge target — sheet numbering stays
  // independent per source działka. The new bundle appears in the list below.
  const createBundle = () => {
    const ids = [...sel];
    if (ids.length < 2) return;
    setError(null);
    bundle.mutate(ids, {
      onSuccess: () => setSel(new Set()),
      onError: (e) => setError(apiErr(e)),
    });
  };

  const startCreate = (mode: "project" | "custom") => {
    if (!selectedVersion) return;
    setError(null);
    setNotice(null);
    create.mutate(
      {
        connection: connection.id,
        route_version: selectedVersion.id,
        mode,
        scale_denominator: 500,
      },
      {
        onSuccess: (set) =>
          announceCreated([set], `Utworzono zestaw „${FORMATKA_MODE_LABEL[mode]}”`),
        onError: (e) => setError(apiErr(e)),
      },
    );
  };

  // Działki PRYWATNE — każda osobno (N zaznaczonych = N zestawów), ale JEDNYM żądaniem
  // (wsad per-parcel). Działka bez naszych obiektów na wybranej wersji trasy nie urywa
  // serii: backend zakłada jej wiersz „Błąd" (bez ramek, z powodem) obok pozostałych,
  // a kolejne działki jadą dalej. Do 16.09.2026 pętla po jednej działce stawała na
  // pierwszym 400 — reszta działek nie dostawała zestawów, a o tym, KTÓRA zawiodła,
  // mówił tylko znikający komunikat. Po utworzeniu ZAWSZE zostajemy na liście: do
  // 25.09.2026 udany wsad otwierał edytor pierwszego zestawu — przy kilku działkach
  // nie było wiadomo, której rozplanowanie widać. Nowe wiersze są oznaczone „nowy",
  // a edytor każdej działki otwiera się ręcznie z jej wiersza.
  const startPrivate = async (parcelIds: string[], opts: PickerOpts) => {
    if (!selectedVersion || parcelIds.length === 0) return;
    setError(null);
    setNotice(null);
    try {
      const res = await createPerParcel.mutateAsync({
        connection: connection.id,
        route_version: selectedVersion.id,
        parcels: parcelIds,
        scale_denominator: 500,
        include_orientation: opts.includeOrientation,
      });
      setPickIntent(null);
      setFresh(new Set(res.sets.map((s) => s.id)));
      const emptyParcels = res.empty_parcels ?? [];
      if (res.failed_count === 0 && emptyParcels.length === 0) {
        const n = res.sets.length;
        announceCreated(res.sets, `Utworzono ${n} ${n === 1 ? "zestaw" : pluralZestawy(n)} — po jednym na działkę`);
        return;
      }
      // Coś wymaga uwagi: zostajemy na liście. Działka bez naszych obiektów NIE jest
      // błędem — dostała arkusz na samej działce i ostrzeżenie na wierszu; błędem jest
      // tylko działka, której nie da się obrysować (bez geometrii).
      const parts = [`Utworzono ${res.ok_count} z ${res.sets.length} zestawów.`];
      if (emptyParcels.length > 0) {
        parts.push(
          `Bez naszych obiektów na wersji ${selectedVersion.label} (${emptyParcels.length}): ` +
            `${emptyParcels.join(", ")} — arkusz obejmuje samą działkę, ostrzeżenie jest na wierszu.`,
        );
      }
      if (res.failed_count > 0) {
        parts.push(
          `Bez arkusza (${res.failed_count}): ${res.failed_parcels.join(", ")} — działki bez geometrii ` +
            "z ewidencji; czerwone wiersze niżej, „Ponów rozplanowanie” próbuje ponownie tylko dla nich.",
        );
      }
      setNotice({ tone: "warn", text: parts.join(" ") });
    } catch (e) {
      setError(apiErr(e));
      setPickIntent(null);
    }
  };

  // Działki URZĘDOWE — po jednym zestawie NA URZĄD: każdy urząd-właściciel to osobny
  // wniosek, więc jego działki dostają własny zestaw (własna numeracja arkuszy, własny
  // arkusz orientacyjny) podpisany nazwą urzędu. Działki jednego urzędu idą wspólnie
  // (jedna kafelkowana mapa). Dotąd wszystkie zaznaczone działki lądowały w JEDNYM
  // zestawie — kilka urzędów na jednym skorowidzu, choć to osobne wnioski. Jak przy
  // prywatnych: zostajemy na liście, edytor każdego urzędu otwiera się z jego wiersza.
  const startOffice = async (parcelIds: string[], opts: PickerOpts) => {
    if (!selectedVersion || parcelIds.length === 0) return;
    setError(null);
    setNotice(null);
    const groups: OfficeGroup[] =
      opts.officeGroups && opts.officeGroups.length > 0
        ? opts.officeGroups
        : [{ key: "", name: "", parcelIds }];
    // Zestawy założone przed ewentualnym błędem kolejnego urzędu też są „nowe" —
    // zostają w bazie, więc mają być widoczne na liście.
    const created: FormatkaSheetSet[] = [];
    try {
      for (const g of groups) {
        const set = await create.mutateAsync({
          connection: connection.id,
          route_version: selectedVersion.id,
          mode: "parcel_office",
          parcels: g.parcelIds,
          scale_denominator: 500,
          include_orientation: opts.includeOrientation,
          // Nazwa urzędu tylko gdy zestawów jest kilka albo urząd jest znany — samotny
          // zestaw „bez urzędu" zostaje z automatycznym tytułem „Działki urzędowe".
          name: g.key ? g.name : groups.length > 1 ? `Działki ${NO_OFFICE_LABEL}` : "",
        });
        created.push(set);
      }
      setPickIntent(null);
      const n = created.length;
      announceCreated(
        created,
        n === 1 ? "Utworzono zestaw działek urzędowych" : `Utworzono ${n} ${pluralZestawy(n)} — po jednym na urząd`,
      );
    } catch (e) {
      setFresh(new Set(created.map((s) => s.id)));
      setError(
        created.length > 0
          ? `Utworzono ${created.length} z ${groups.length} zestawów urzędowych (oznaczone „nowy”), kolejny się nie powiódł: ${apiErr(e)}`
          : apiErr(e),
      );
      setPickIntent(null);
    }
  };

  return (
    <SectionCard title="Formatki (plan sytuacyjny)">
      <div className="p-4 space-y-3">
        <p className="text-[11px] text-[var(--color-text-muted)] leading-snug">
          Arkusze A4 z viewportem (layouty paper space w DXF) + skrypt plotujący
          do jednego PDF. Rozmieszczenie arkuszy ustawisz w edytorze na mapie.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!selectedVersion || creating}
            title={!selectedVersion ? "Wgraj najpierw wersję trasy" : undefined}
            onClick={() => startCreate("project")}
          >
            Nowy — cały projekt
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!selectedVersion || creating}
            title={!selectedVersion ? "Wgraj najpierw wersję trasy" : "Każda działka osobno — własny zestaw i mapa"}
            onClick={() => setPickIntent("private")}
          >
            Nowy — działki prywatne
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!selectedVersion || creating}
            title={!selectedVersion ? "Wgraj najpierw wersję trasy" : "Po jednym zestawie na urząd — działki tego samego urzędu na wspólnej mapie"}
            onClick={() => setPickIntent("office")}
          >
            Nowy — działki urzędowe
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="col-span-2"
            disabled={!selectedVersion || creating}
            title={!selectedVersion ? "Wgraj najpierw wersję trasy" : undefined}
            onClick={() => startCreate("custom")}
          >
            Nowy — własny zakres
          </Button>
        </div>
        {!selectedVersion && (
          <p className="text-[10px] font-mono text-amber-600">
            Formatki wymagają wgranej wersji trasy (DXF).
          </p>
        )}
        {error && <p className="text-[11px] font-mono text-[var(--color-primary)]">{error}</p>}
        {notice && (
          <p
            className={clsx(
              "flex items-start gap-1.5 rounded-md border px-2.5 py-2 text-[11px]",
              notice.tone === "ok"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-800",
            )}
          >
            {notice.tone === "ok" ? (
              <Check size={13} className="mt-[1px] shrink-0" />
            ) : (
              <AlertTriangle size={13} className="mt-[1px] shrink-0" />
            )}
            <span className="flex-1">{notice.text}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="shrink-0 opacity-60 hover:opacity-100"
              title="Zamknij komunikat"
            >
              <X size={12} />
            </button>
          </p>
        )}

        {isLoading ? (
          <div className="py-2"><Spinner /></div>
        ) : (sheetSets ?? []).length === 0 ? (
          <p className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] text-center py-2">
            Brak zestawów
          </p>
        ) : (
          <>
            {newestParcelSets.length >= 2 && (
              <button
                type="button"
                onClick={toggleAllParcels}
                className={clsx(CHIP, "w-full justify-center border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]")}
                title="Zaznacza najnowszy zestaw każdej działki — gotowe do połączenia w paczkę"
              >
                <CheckSquare size={13} className="mr-1" />
                {allParcelsSelected
                  ? "Odznacz wszystkie działki"
                  : `Zaznacz wszystkie działki (${newestParcelSets.length})`}
              </button>
            )}
            {sel.size >= 2 && (
              <div className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50/60 px-2.5 py-2">
                <Button size="sm" loading={bundle.isPending} onClick={createBundle}>
                  <Layers size={13} className="mr-1" /> Połącz w paczkę ({sel.size})
                </Button>
                <button type="button" onClick={() => setSel(new Set())} className="text-[11px] font-medium text-violet-700 hover:underline">
                  wyczyść
                </button>
                <span className="ml-auto text-[10px] text-violet-600/80">
                  jeden ZIP i upload · numeracja per działka
                </span>
              </div>
            )}
            {/* Wiersze z błędem — jeden baner, dwa rodzaje ponowienia: działki bez arkusza
                (błąd rozplanowania) ponawia się na wersji trasy wybranej nad mapą, zestawy
                bez paczki ZIP (błąd generowania) — zwykłym generate. Pojedynczo: chip na
                czerwonym wierszu. */}
            {(layoutFailed.length > 0 || zipFailed.length > 0) && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50/70 px-2.5 py-2">
                <AlertTriangle size={13} className="shrink-0 text-red-600" />
                <span className="text-[11px] font-medium text-red-800">
                  {[
                    layoutFailed.length > 0 && `${pluralDzialki(layoutFailed.length)} bez arkusza (błąd rozplanowania)`,
                    zipFailed.length > 0 &&
                      `${zipFailed.length} ${zipFailed.length === 1 ? "zestaw" : pluralZestawy(zipFailed.length)} bez paczki ZIP (błąd generowania)`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}{" "}
                  <span className="font-normal">— czerwone wiersze niżej</span>
                </span>
                <span className="ml-auto flex flex-wrap items-center gap-1.5">
                  {layoutFailed.length > 0 && (
                    <button
                      type="button"
                      disabled={!selectedVersion || retryAll.isPending}
                      onClick={retryAllFailed}
                      className={clsx(CHIP, "border-red-300 bg-white text-red-700 hover:bg-red-100")}
                      title={
                        selectedVersion
                          ? `Ponów rozplanowanie wszystkich działek z błędem na wersji ${selectedVersion.label} — pozostałe zestawy zostają nietknięte`
                          : "Wybierz wersję trasy nad mapą"
                      }
                    >
                      <RefreshCcw size={12} />{" "}
                      {retryAll.isPending
                        ? "Ponawiam…"
                        : `Ponów rozplanowanie (${layoutFailed.length})${selectedVersion ? ` na ${selectedVersion.label}` : ""}`}
                    </button>
                  )}
                  {zipFailed.length > 0 && (
                    <button
                      type="button"
                      disabled={regenerateAll.isPending}
                      onClick={regenerateAllFailed}
                      className={clsx(CHIP, "border-red-300 bg-white text-red-700 hover:bg-red-100")}
                      title="Uruchom generowanie paczki ZIP jeszcze raz dla każdego zestawu z błędem generowania — te same ramki i szablon"
                    >
                      <RefreshCcw size={12} />{" "}
                      {regenerateAll.isPending ? "Uruchamiam…" : `Ponów generowanie ZIP (${zipFailed.length})`}
                    </button>
                  )}
                </span>
              </div>
            )}
            <ul className="space-y-2">
              {(sheetSets ?? []).map((s) => (
                <SheetSetRow
                  key={s.id}
                  sheetSet={s}
                  selectedVersion={selectedVersion}
                  selected={sel.has(s.id)}
                  fresh={fresh.has(s.id)}
                  onToggle={() => toggleSel(s.id)}
                  onOpen={() => navigate(`/power-projects/${connection.project}/formatki/${s.id}`)}
                  // Deleting a set is REFUSED for anyone but its author (or dyrektor/CEO),
                  // and while a generation runs. Until that refusal was rendered the trash
                  // icon read as dead: request out, 403/400 back, nothing on screen.
                  onDelete={() => {
                    if (!window.confirm("Usunąć ten zestaw formatek?")) return;
                    setError(null);
                    remove.mutate(s.id, {
                      onError: (e) => setError(apiErr(e, "Nie udało się usunąć zestawu formatek.")),
                    });
                  }}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      {pickIntent && selectedVersion && (
        <ParcelPickerModal
          connectionId={connection.id}
          routeVersion={selectedVersion}
          intent={pickIntent}
          loading={creating}
          onClose={() => setPickIntent(null)}
          onConfirm={pickIntent === "office" ? startOffice : startPrivate}
        />
      )}
    </SectionCard>
  );
}

/** One sheet-set row: open editor, download ZIP, then the merged-PDF flow —
 *  upload the plotted per-sheet PDFs (backend merges), download the merged PDF,
 *  browse the version archive, and (tech director) overwrite with a chosen PDF. */
function SheetSetRow({
  sheetSet: s,
  selectedVersion,
  selected,
  fresh,
  onToggle,
  onOpen,
  onDelete,
}: {
  sheetSet: FormatkaSheetSet;
  // Wersja trasy wybrana nad mapą — na niej ponawia się rozplanowanie działki z błędem
  // (typowo inna niż ta, na której wsad nie znalazł obiektów).
  selectedVersion: RouteVersion | undefined;
  selected: boolean;
  // Utworzony ostatnim „Nowy — …" na tej karcie — znacznik „nowy" przy tytule.
  fresh: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const upload = useUploadSheetPdfs();
  const overwrite = useOverwriteSheetPdf();
  const generate = useGenerateSheetSet(s.id, s.connection);
  const retry = useRetrySheetSet(s.id);
  const rename = useUpdateSheetSet(s.id);
  // Ukrycie ostrzeżeń generatora — to samo PATCH co zmiana nazwy, osobna instancja,
  // żeby stan „w toku" i błąd nie mieszały się z edycją nazwy.
  const dismiss = useUpdateSheetSet(s.id);
  const [dismissing, setDismissing] = useState(false);
  const [dismissNote, setDismissNote] = useState("Poprawione ręcznie w CAD");
  // Ukryte ostrzeżenia da się podejrzeć bez przywracania (jedna linijka → pełna lista).
  const [showDismissed, setShowDismissed] = useState(false);
  const confirmDismiss = () =>
    dismiss.mutate(
      { warnings_dismissed: true, warnings_dismissed_note: dismissNote.trim() },
      { onSuccess: () => { setDismissing(false); setShowDismissed(false); } },
    );
  const restoreWarnings = () => dismiss.mutate({ warnings_dismissed: false });
  const dismissedLabel = s.warnings_dismissed_at
    ? `Ukryte${s.warnings_dismissed_by_name ? ` przez ${s.warnings_dismissed_by_name}` : ""} ` +
      `(${fmtDateTime(s.warnings_dismissed_at)})${s.warnings_dismissed_note ? `: ${s.warnings_dismissed_note}` : ""}`
    : "";
  const layoutFailed = isLayoutFailure(s);
  const zipFailed = isZipFailure(s);
  // Ponowienie na wersji innej niż ta z wiersza — mówimy to wprost na chipie.
  const retryVersion = selectedVersion && selectedVersion.id !== s.route_version ? selectedVersion : undefined;
  const mergeInput = useRef<HTMLInputElement>(null);
  const overwriteInput = useRef<HTMLInputElement>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(s.name);
  const busy = upload.isPending || overwrite.isPending;
  const versions = s.pdf_versions ?? [];
  const autoTitle =
    s.mode === "parcel"
      ? `Działka ${s.parcel_label ?? "?"}`
      : s.mode === "parcel_office"
        ? `Działki urzędowe (${s.source_parcel_count})`
        : FORMATKA_MODE_LABEL[s.mode];
  const title = s.name || autoTitle;
  const saveName = () => {
    const next = nameDraft.trim();
    if (next === s.name) { setEditingName(false); return; }
    rename.mutate({ name: next }, { onSettled: () => setEditingName(false) });
  };

  // Descriptive download name so a single-parcel („private") map stays identifiable
  // even after the group was bundled — with the route version noted, as requested.
  const slug = (t: string) =>
    t.normalize("NFKD").replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  const parcelTag = s.mode === "parcel" && s.parcel_label ? `_dz-${slug(s.parcel_label)}` : "";
  const routeTag = s.route_version_label ? `_${slug(s.route_version_label)}` : "";
  const downloadName = (ext: string) => `PLAN_SYT${parcelTag}${routeTag}.${ext}`;

  const badge = MODE_BADGE[s.mode];
  const pill = STATUS_PILL[s.status];
  const BadgeIcon = badge.icon;

  return (
    <li
      className={clsx(
        "group relative overflow-hidden rounded-lg border transition-all hover:shadow-sm",
        s.status === "failure"
          ? "border-red-300 bg-red-50/40 hover:border-red-400"
          : "border-[var(--color-border-muted)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-strong)]",
      )}
    >
      <span className={clsx("absolute inset-y-0 left-0 w-[3px]", badge.accent)} aria-hidden />
      <div className="py-2.5 pl-3.5 pr-2.5">
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--color-primary)] shrink-0 disabled:opacity-30"
            checked={selected}
            onChange={onToggle}
            disabled={s.mode === "bundle" || layoutFailed}
            title={
              s.mode === "bundle"
                ? "Paczki nie można łączyć w kolejną paczkę"
                : layoutFailed
                  ? "Zestaw bez arkuszy — najpierw ponów rozplanowanie"
                  : "Zaznacz do połączenia w paczkę"
            }
          />
          <div className="min-w-0 flex-1">
            {editingName ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") { setNameDraft(s.name); setEditingName(false); }
                  }}
                  placeholder={autoTitle}
                  maxLength={200}
                  className="input-base text-xs py-1 flex-1 min-w-0"
                />
                <button type="button" onClick={saveName} disabled={rename.isPending} className="p-1 text-emerald-600 hover:bg-[var(--color-bg-elevated)] disabled:opacity-40" title="Zapisz nazwę">
                  <Check size={14} />
                </button>
                <button type="button" onClick={() => { setNameDraft(s.name); setEditingName(false); }} className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" title="Anuluj">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className={clsx("shrink-0 inline-flex items-center gap-1 rounded border px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wide", badge.chip)}>
                  <BadgeIcon size={10} /> {badge.label}
                </span>
                <button
                  type="button"
                  onClick={onOpen}
                  title="Otwórz rozplanowanie arkuszy"
                  className="min-w-0 truncate text-left text-[13px] font-semibold text-[var(--color-text-primary)] transition-colors hover:text-[var(--color-primary)]"
                >
                  {title}
                </button>
                {fresh && (
                  <span
                    className="shrink-0 rounded bg-emerald-100 px-1 py-[1px] text-[9px] font-semibold uppercase tracking-wide text-emerald-700"
                    title="Utworzony właśnie teraz — otwórz rozplanowanie arkuszy tego zestawu"
                  >
                    nowy
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => { setNameDraft(s.name); setEditingName(true); }}
                  className="shrink-0 p-0.5 text-[var(--color-text-muted)] opacity-0 transition-opacity hover:text-[var(--color-primary)] group-hover:opacity-100"
                  title="Zmień nazwę zestawu"
                >
                  <Pencil size={12} />
                </button>
              </div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] font-mono text-[var(--color-text-muted)]">
              <span className="font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                {FORMATKA_TYPE_LABEL[s.formatka_type]}
              </span>
              <span className="opacity-40">·</span>
              <span>{s.frames.length} ark.</span>
              <span className="opacity-40">·</span>
              <span>1:{s.scale_denominator}</span>
              {s.route_version_label && (
                <>
                  <span className="opacity-40">·</span>
                  <span>{s.route_version_label}</span>
                </>
              )}
              {/* Gmina, dla której powstaje mapa — na jednym terenie bywają DWIE różne
                  gminy, więc bez tej etykiety łatwo podpiąć mapę pod zły urząd.
                  Kilka gmin w jednym zestawie urzędowym = ostrzegawczo (bursztyn). */}
              {(s.communes ?? []).length > 0 && (
                <>
                  <span className="opacity-40">·</span>
                  <span
                    className={clsx(
                      s.mode === "parcel_office" && (s.communes ?? []).length > 1 && "font-semibold text-amber-600",
                    )}
                    title={`Gmina, dla której generowana jest mapa: ${(s.communes ?? []).join(", ")}`}
                  >
                    gm. {(s.communes ?? []).join(", ")}
                  </span>
                </>
              )}
              <span className="opacity-40">·</span>
              <span>{fmtDateTime(s.created_at)}</span>
              {/* Who created the set — they are (besides a dyrektor/CEO) the one who may
                  delete it, so without this label there is no way to tell whom to ask. */}
              {s.created_by_name && (
                <>
                  <span className="opacity-40">·</span>
                  <span title={`Zestaw utworzył(a): ${s.created_by_name}`}>
                    {s.created_by_name}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className={clsx("inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10px] font-semibold", pill.cls)}>
              {pill.dot && <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}
              {pill.label}
            </span>
            {s.status === "success" && s.output_file?.url && (
              <a
                href={s.output_file.url}
                download={downloadName("zip")}
                className="rounded p-1 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-primary)]"
                title={`Pobierz ZIP (DXF + skrypt plotu)${s.route_version_label ? ` — ${s.route_version_label}` : ""}`}
              >
                <Download size={14} />
              </a>
            )}
            {/* Only on sets the server will actually let this user delete (author,
                dyrektor, CEO) — otherwise the kosz was there for everyone and every
                click on someone else's set ended in a 403. */}
            {s.can_delete && (
              <button
                type="button"
                onClick={onDelete}
                className="rounded p-1 text-[var(--color-border-strong)] transition-colors hover:bg-red-50 hover:text-red-600"
                title="Usuń zestaw"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>

        {/* ── ostrzeżenia generatora ──
            Skala arkuszy jest wyborem człowieka: to, co się w niej nie mieści (opisy,
            obiekty), albo komplet działki z mieszanymi skalami, generator POKAZUJE, a nie
            poprawia po cichu. Decyzja (dołożyć arkusz, przesunąć ramkę, zmienić skalę)
            należy do użytkownika — stąd blok na karcie, nie tylko wpis w logu zadania.
            Gdy poprawka poszła RĘCZNIE (projektant dołożył opisy w CAD-zie i wgrał gotowe
            PDF-y), generator o tym nie wie — człowiek ukrywa ostrzeżenia z podpisem i
            notatką; lista zostaje do podejrzenia, a ukrycie znika przy nowym układzie
            ramek lub generowaniu. */}
        {(s.warnings ?? []).length > 0 && (
          s.warnings_dismissed_at && !showDismissed ? (
            <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-[var(--color-text-muted)]">
              <EyeOff size={11} className="shrink-0" />
              <span title={dismissedLabel}>
                Ostrzeżenia generatora ({s.warnings.length}) {dismissedLabel.charAt(0).toLowerCase() + dismissedLabel.slice(1)}
              </span>
              <button type="button" onClick={() => setShowDismissed(true)} className="underline hover:text-[var(--color-primary)]">
                pokaż
              </button>
              <button type="button" disabled={dismiss.isPending} onClick={restoreWarnings} className="underline hover:text-[var(--color-primary)]">
                przywróć
              </button>
            </p>
          ) : (
            <div
              className={clsx(
                "mt-2 rounded-md border px-2 py-1.5 text-[11px]",
                s.warnings_dismissed_at
                  ? "border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]"
                  : "border-amber-200 bg-amber-50 text-amber-800",
              )}
              title="Ostrzeżenia z ostatniego generowania — znikają po zmianie ramek i ponownym wygenerowaniu"
            >
              <ul className="space-y-1">
                {(s.warnings ?? []).map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <AlertTriangle size={12} className="mt-[1px] shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
              {s.warnings_dismissed_at ? (
                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[10px]">
                  <span>{dismissedLabel}</span>
                  <button type="button" onClick={() => setShowDismissed(false)} className="underline hover:text-[var(--color-primary)]">
                    schowaj
                  </button>
                  <button type="button" disabled={dismiss.isPending} onClick={restoreWarnings} className="underline hover:text-[var(--color-primary)]">
                    przywróć
                  </button>
                </p>
              ) : dismissing ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <input
                    autoFocus
                    value={dismissNote}
                    onChange={(e) => setDismissNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmDismiss();
                      if (e.key === "Escape") setDismissing(false);
                    }}
                    maxLength={255}
                    placeholder="Dlaczego? np. opisy dołożone ręcznie w CAD, PDF wgrany"
                    className="input-base min-w-[14rem] flex-1 py-0.5 text-[11px]"
                  />
                  <button
                    type="button"
                    disabled={dismiss.isPending}
                    onClick={confirmDismiss}
                    className={clsx(CHIP, "border-amber-400 bg-white text-amber-800 hover:bg-amber-100")}
                  >
                    <EyeOff size={11} /> {dismiss.isPending ? "Ukrywam…" : "Ukryj"}
                  </button>
                  <button type="button" onClick={() => setDismissing(false)} className="text-[10px] underline hover:text-[var(--color-primary)]">
                    Anuluj
                  </button>
                </div>
              ) : (
                <p className="mt-1 flex justify-end text-[10px]">
                  <button
                    type="button"
                    onClick={() => setDismissing(true)}
                    className="inline-flex items-center gap-1 underline hover:text-amber-900"
                    title="Ostrzeżenia zostają w historii zestawu (do podejrzenia), ale znikają z karty — do następnego generowania lub zmiany ramek"
                  >
                    <EyeOff size={11} /> Ukryj — poprawione ręcznie (np. w CAD)
                  </button>
                </p>
              )}
            </div>
          )
        )}

        {/* ── błąd ──
            Powód z serwera, nie tylko pigułka „Błąd": przy błędzie ROZPLANOWANIA (wsad per
            działka nie znalazł naszych obiektów na tej wersji trasy) to jedyne miejsce,
            gdzie widać, na której wersji i na jakiej działce zawiodło, a przy błędzie
            GENEROWANIA — dlaczego paczka ZIP nie powstała. */}
        {s.status === "failure" && s.error && (
          <p
            className="mt-2 flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-800"
            title={layoutFailed ? "Błąd rozplanowania arkuszy" : "Błąd generowania paczki ZIP"}
          >
            <AlertTriangle size={12} className="mt-[1px] shrink-0" />
            <span>
              <b>{layoutFailed ? "Błąd rozplanowania:" : "Błąd generowania:"}</b> {s.error}
            </span>
          </p>
        )}

        {/* ── akcje (chipy) ── */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* Edytor rozplanowania otwiera się tylko stąd (tworzenie zestawu nie wchodzi
              do niego samo) — więc wprost jako akcja, nie tylko klikalny tytuł. */}
          <button
            type="button"
            onClick={onOpen}
            className={clsx(CHIP, "border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]")}
            title={`Otwórz rozplanowanie arkuszy — ${title}`}
          >
            <LayoutGrid size={12} /> Rozplanowanie
          </button>
          {s.status === "generating" ? (
            <span className={clsx(CHIP, "border-amber-200 bg-amber-50 text-amber-700")}>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> Generowanie ZIP…
            </span>
          ) : layoutFailed ? (
            <>
              <button
                type="button"
                disabled={retry.isPending}
                onClick={() => retry.mutate(selectedVersion?.id)}
                className={clsx(CHIP, "border-red-300 bg-white text-red-700 hover:bg-red-100")}
                title={
                  retryVersion
                    ? `Rozplanuj tę działkę ponownie na wersji ${retryVersion.label} (wybranej nad mapą) — pozostałe zestawy zostają nietknięte`
                    : `Rozplanuj tę działkę ponownie na wersji ${s.route_version_label ?? "trasy"} — pozostałe zestawy zostają nietknięte`
                }
              >
                <RefreshCcw size={12} />{" "}
                {retry.isPending
                  ? "Ponawiam…"
                  : `Ponów rozplanowanie${retryVersion ? ` na ${retryVersion.label}` : ""}`}
              </button>
              {retry.isSuccess && retry.data?.status === "failure" && (
                <span className="text-[10px] text-red-700">
                  Nadal bez arkusza — zmień wersję trasy nad mapą albo rozszerz ramkę zestawu
                  działki sąsiedniej w edytorze.
                </span>
              )}
            </>
          ) : s.status !== "success" && s.frames.length === 0 ? (
            <span className="text-[10px] text-[var(--color-text-muted)]">
              Otwórz edytor i rozmieść ramki, aby wygenerować.
            </span>
          ) : s.status !== "success" ? (
            // Po błędzie generowania ten sam POST generate jest PONOWIENIEM — chip mówi to
            // wprost, bo „Generuj ZIP" pod czerwonym komunikatem czytało się jak pierwsze
            // uruchomienie, a nie jak „spróbuj jeszcze raz".
            <button
              type="button"
              disabled={generate.isPending}
              onClick={() => generate.mutate()}
              className={clsx(
                CHIP,
                zipFailed
                  ? "border-red-300 bg-white text-red-700 hover:bg-red-100"
                  : "border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-bg-elevated)]",
              )}
              title={
                zipFailed
                  ? "Uruchom generowanie paczki ZIP jeszcze raz — te same ramki i szablon"
                  : "Zbuduj ZIP (DXF + skrypt plotu). Potem pojawi się wrzucanie PDF-ów."
              }
            >
              {zipFailed ? <RefreshCcw size={12} /> : <Play size={12} />}{" "}
              {generate.isPending ? "Uruchamiam…" : zipFailed ? "Ponów generowanie ZIP" : "Generuj ZIP"}
            </button>
          ) : null}

          {/* Mapa (scalony PDF) — ZAWSZE gdy istnieje, też dla rozdzielonych z paczki. */}
          {s.merged_pdf?.url && (
            <a
              href={s.merged_pdf.url}
              download={downloadName("pdf")}
              className={clsx(CHIP, "border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-bg-elevated)]")}
              title={`Pobierz mapę (PDF)${s.route_version_label ? ` — ${s.route_version_label}` : ""}`}
            >
              <Download size={12} /> {s.mode === "parcel" ? "Mapa działki" : "Mapa"}
              {versions.length ? ` v${versions[0].version}` : ""}
            </a>
          )}

          {/* „Wrzuć PDF-y" wymaga ZIP-a (success). */}
          {s.status === "success" && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => mergeInput.current?.click()}
                className={clsx(CHIP, "border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]")}
                title={
                  s.mode === "bundle"
                    ? "Wrzuć splotowane PDF-y albo cały ZIP — backend scali paczkę i rozdzieli arkusze na zestawy źródłowe (działki)"
                    : "Wrzuć PDF-y arkuszy (PLAN_SYTUACYJNY_NN.pdf) albo cały ZIP — backend scali je w jeden"
                }
              >
                <FileUp size={12} /> {upload.isPending ? "Scalam…" : "Wrzuć PDF-y"}
              </button>
              <input
                ref={mergeInput}
                type="file"
                accept=".pdf,.zip"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (files.length) upload.mutate({ id: s.id, files });
                }}
              />
            </>
          )}

          {s.can_overwrite_pdf && (s.merged_pdf?.url || s.status === "success") && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => overwriteInput.current?.click()}
                className={clsx(CHIP, "border-[var(--color-border-strong)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]")}
                title="Nadpisz scalony PDF własnym plikiem (nowa wersja; stare zostają w archiwum)"
              >
                <Replace size={12} /> {overwrite.isPending ? "Zapisuję…" : "Nadpisz"}
              </button>
              <input
                ref={overwriteInput}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) overwrite.mutate({ id: s.id, file });
                }}
              />
            </>
          )}

          {versions.length > 1 && (
            <button
              type="button"
              onClick={() => setShowVersions((v) => !v)}
              className={clsx(CHIP, "border-transparent text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-primary)]")}
            >
              <History size={12} /> Wersje ({versions.length})
            </button>
          )}

          {s.status === "success" && !s.merged_pdf?.url && (
            <span className="text-[10px] text-[var(--color-text-muted)]">wrzuć arkusze, aby scalić mapę</span>
          )}
          {(generate.isError || upload.isError || overwrite.isError || retry.isError || dismiss.isError) && (
            <span className="text-[10px] text-[var(--color-primary)]">
              {apiErr(generate.error ?? upload.error ?? overwrite.error ?? retry.error ?? dismiss.error)}
            </span>
          )}
        </div>

        {showVersions && versions.length > 0 && (
          <ul className="mt-2 space-y-1 border-t border-[var(--color-border-muted)] pt-2">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-2 text-[10px] font-mono text-[var(--color-text-muted)]">
                <span className="w-7 font-semibold text-[var(--color-text-secondary)]">v{v.version}</span>
                <span className={clsx("rounded px-1 py-[1px]", v.kind === "overwrite" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500")}>
                  {v.kind === "overwrite" ? "nadpisany" : "scalony"}
                </span>
                <span className="truncate">{fmtDateTime(v.created_at)}{v.created_by_name ? ` · ${v.created_by_name}` : ""}</span>
                {v.file?.url && (
                  <a href={v.file.url} download={v.file.original_filename} className="ml-auto text-[var(--color-primary)] hover:underline">
                    pobierz
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/** Pick which parcels to make formatki for. The backend seeds one sheet per
 *  parcel, framed on OUR objects there (route + map objects), not the whole
 *  parcel. `intent` decides grouping: "private" → one set per parcel; "office"
 *  → all parcels in one shared set. */
function ParcelPickerModal({
  connectionId,
  routeVersion,
  intent,
  loading,
  onClose,
  onConfirm,
}: {
  connectionId: string;
  routeVersion: RouteVersion;
  intent: "private" | "office";
  loading: boolean;
  onClose: () => void;
  onConfirm: (parcelIds: string[], opts: PickerOpts) => void;
}) {
  const office = intent === "office";
  const { data: parcels, isLoading } = useParcels(connectionId);
  // Działki tej WERSJI trasy. Lista działek jest wspólna dla przyłączenia (jeden
  // worek na wszystkie warianty, a `in_current_route` mówi tylko o ostatnim
  // przeliczeniu), więc bez tego zawężenia do wspólnego zestawu wchodziły działki
  // wariantu alternatywnego — bez trasy, czyli i bez arkusza, ale zawyżając liczbę
  // działek i listę gmin zestawu.
  const { data: versionParcelIds, isLoading: idsLoading } = useRouteVersionParcelIds(routeVersion.id);
  const onVersion = useMemo(() => new Set(versionParcelIds ?? []), [versionParcelIds]);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [ownSel, setOwnSel] = useState<Set<string>>(new Set());
  const [unitSel, setUnitSel] = useState<Set<string>>(new Set());
  // Arkusz orientacyjny (rozkład arkuszy) — domyślnie tak, jak dotąd; tu można go
  // wyłączyć już przy tworzeniu, bez wchodzenia w modal generowania każdego zestawu.
  const [includeOrientation, setIncludeOrientation] = useState(true);

  // Pula wyszukiwania = działki projektu (po TERYT można dodać KAŻDĄ, także spoza
  // wariantu — świadomie, z ostrzeżeniem na wierszu). Domyślna lista jest węższa:
  // tylko działki wybranej wersji trasy (`versionPool` niżej).
  const pool = useMemo(
    () => (parcels ?? []).filter((p: Parcel) => p.in_current_route || p.is_manual || onVersion.has(p.id)),
    [parcels, onVersion],
  );

  const versionPool = useMemo(() => pool.filter((p) => onVersion.has(p.id)), [pool, onVersion]);

  // Wartości filtrów bierzemy z działek TEJ kategorii NA TEJ wersji trasy (urzędowych
  // albo prywatnych) — chipy pokazują więc dokładnie to, co domyślnie widać na liście.
  const categoryPool = useMemo(
    () => versionPool.filter((p) => (office ? isOfficeParcel(p) : isPrivateParcel(p))),
    [versionPool, office],
  );

  const ownershipFacets = useMemo<Facet[]>(
    () =>
      [...countFacet(categoryPool, (p) => (p.ownership_code ?? "").trim())]
        .map(([value, count]) => ({
          value,
          label: findOwnershipOption(value)?.label || value || "bez kodu",
          count,
          color: ownershipCodeColor(value),
        }))
        .sort(
          (a, b) =>
            ownershipRank(a.value) - ownershipRank(b.value) || a.label.localeCompare(b.label, "pl"),
        ),
    [categoryPool],
  );

  const unitFacets = useMemo<Facet[]>(
    () =>
      [...countFacet(categoryPool, parcelJednostka)]
        .map(([value, count]) => ({ value, label: value || "bez jednostki", count }))
        .sort((a, b) => (a.value ? 0 : 1) - (b.value ? 0 : 1) || a.label.localeCompare(b.label, "pl")),
    [categoryPool],
  );

  const anyFilter = ownSel.size > 0 || unitSel.size > 0;
  const clearFilters = () => {
    setOwnSel(new Set());
    setUnitSel(new Set());
  };
  const toggleIn = (set: (fn: (prev: Set<string>) => Set<string>) => void) => (value: string) =>
    set((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });

  // Default list = only the działki this modal is about (prywatne dla trybu prywatnego,
  // urzędowe/nieprywatne dla urzędowego) LEŻĄCE NA WYBRANEJ WERSJI TRASY, zawężone
  // filtrami kodu własności / jednostki ewidencyjnej. Każdą działkę można jednak dodać:
  // wpisanie TERYT szuka po WSZYSTKICH działkach (także spoza kategorii i spoza
  // wariantu), a zaznaczona działka zostaje na liście aż do odznaczenia — nawet po
  // wyczyszczeniu wyszukiwania, filtrów lub gdy jest spoza kategorii.
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    const matchesSearch = (p: Parcel) =>
      (p.teryt + " " + (p.short_number ?? "")).toLowerCase().includes(t);
    const inCategory = (p: Parcel) => (office ? isOfficeParcel(p) : isPrivateParcel(p));
    const matchesFilters = (p: Parcel) =>
      (ownSel.size === 0 || ownSel.has((p.ownership_code ?? "").trim())) &&
      (unitSel.size === 0 || unitSel.has(parcelJednostka(p)));
    return pool.filter((p) => {
      if (sel.has(p.id)) return true; // zaznaczona — widoczna do odznaczenia
      if (!matchesFilters(p)) return false; // filtry działają też przy wyszukiwaniu
      if (t) return matchesSearch(p); // wyszukiwanie po TERYT — po wszystkich działkach
      return onVersion.has(p.id) && inCategory(p); // domyślnie: ta wersja trasy + kategoria
    });
  }, [pool, q, office, sel, ownSel, unitSel, onVersion]);

  const toggle = (id: string) =>
    setSel((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allShown = rows.length > 0 && rows.every((p) => sel.has(p.id));
  const toggleAll = () =>
    setSel((prev) => {
      const next = new Set(prev);
      if (allShown) rows.forEach((p) => next.delete(p.id));
      else rows.forEach((p) => next.add(p.id));
      return next;
    });

  // Gminy ZAZNACZONYCH działek — jedna trasa potrafi przechodzić przez DWIE różne
  // gminy, więc przed utworzeniem wspólnej mapy dla urzędu musi być widać, dla
  // jakiej gminy (albo ilu gmin naraz) ona powstanie.
  const selCommunes = useMemo(() => {
    const names = new Set<string>();
    (parcels ?? []).forEach((p: Parcel) => {
      if (sel.has(p.id) && (p.commune ?? "").trim()) names.add(p.commune.trim());
    });
    return [...names].sort((a, b) => a.localeCompare(b, "pl"));
  }, [parcels, sel]);

  // Podział zaznaczenia po urzędach — tyle zestawów powstanie. Widoczny przed
  // kliknięciem, bo dotąd wszystko szło w jeden zestaw i człowiek dowiadywał się o
  // pomieszanych urzędach dopiero na wygenerowanym skorowidzu.
  const officeGroups = useMemo(
    () => (office ? officeGroupsOf(parcels ?? [], sel) : []),
    [office, parcels, sel],
  );

  // Szerszy modal (lg): wiersz niesie cztery kolumny — działka, grupa własności,
  // właściciel, jednostka ewidencyjna — plus znaczniki; w wąskim nazwiska ucinałyby
  // się do kilku liter.
  return (
    <Modal
      open
      onClose={onClose}
      title={office ? "Działki urzędowe — po jednym zestawie na urząd" : "Działki prywatne — każda osobno"}
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-[12px] text-[var(--color-text-secondary)]">
          {office ? (
            <>
              Zaznaczone działki dzielą się <b>po urzędzie właściciela</b>: każdy urząd to
              osobny wniosek, więc dostaje <b>własny zestaw</b> (własna numeracja arkuszy i
              własny arkusz orientacyjny), a działki tego samego urzędu idą na jedną wspólną
              mapę. Działka współwłasna dwóch urzędów trafia do obu zestawów.
            </>
          ) : (
            <>
              Każda zaznaczona działka dostanie <b>osobny</b> zestaw i osobną mapę
              (własna korespondencja/właściciel). Po utworzeniu zestawy pojawią się na
              liście, podpisane numerem działki — rozplanowanie arkuszy każdej otworzysz
              z jej wiersza.
            </>
          )}{" "}
          Arkusz obejmuje <b>nasze obiekty</b> na działce (trasę + elementy), nie całą
          działkę — skala dobierana automatycznie. Wszystko poprawisz w edytorze.{" "}
          Na liście domyślnie tylko działki <b>{office ? "urzędowe" : "prywatne"}</b> (wg kategorii
          uzgodnienia; grunt w użytkowaniu wieczystym prywatnego użytkownika jest na obu listach){" "}
          leżące na wybranej wersji trasy (<b>{routeVersion.label}</b>) — działki innych
          wariantów nie mają tu trasy, więc nie dostałyby arkusza; wpisz TERYT, aby dodać inną.{" "}
          {office && (
            <>
              Filtry zawężają listę — np. jedna jednostka ewidencyjna i „Zaznacz widoczne”.
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          <input
            className="input-base text-sm py-1.5 flex-1"
            placeholder="Szukaj / dodaj po nr / TERYT…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="button" onClick={toggleAll} className="text-[11px] font-mono text-[var(--color-primary)] hover:underline whitespace-nowrap">
            {allShown ? "Odznacz widoczne" : "Zaznacz widoczne"}
          </button>
        </div>

        {/* ── Filtry: kod własności + jednostka ewidencyjna (wartości z tego projektu) ── */}
        {(ownershipFacets.length > 1 || unitFacets.length > 1) && (
          <div className="space-y-1.5 border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-2.5 py-2">
            <div className="flex items-center gap-1.5 text-[var(--color-text-muted)]">
              <Filter size={12} />
              <span className="section-label">Filtry</span>
              {anyFilter && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="ml-auto text-[11px] font-mono text-[var(--color-primary)] hover:underline"
                >
                  Wyczyść filtry
                </button>
              )}
            </div>
            {ownershipFacets.length > 1 && (
              <FacetChips
                label="Kod własności"
                options={ownershipFacets}
                selected={ownSel}
                onToggle={toggleIn(setOwnSel)}
              />
            )}
            {unitFacets.length > 1 && (
              <FacetChips
                label="Jednostka ewid."
                options={unitFacets}
                selected={unitSel}
                onToggle={toggleIn(setUnitSel)}
              />
            )}
          </div>
        )}

        {isLoading || idsLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : (
          <div className="max-h-[46vh] overflow-y-auto border border-[var(--color-border-muted)] divide-y divide-[var(--color-border-muted)]">
            {rows.map((p) => {
              const opt = findOwnershipOption(p.ownership_code);
              const unit = parcelJednostka(p);
              // "Poza kategorią" = działka nie należy do tego modalu (widoczna bo zaznaczona
              // lub znaleziona przez wyszukanie) — oznaczamy ją, by było jasne, czemu tu jest.
              const offCategory = office ? !isOfficeParcel(p) : !isPrivateParcel(p);
              // Poza wybraną wersją trasy (np. działka wariantu alternatywnego, dodana
              // przez wyszukanie) — nie ma na niej ani trasy, ani obiektu, więc backend
              // nie zrobi dla niej arkusza i nie wliczy jej do zestawu.
              const offVersion = !onVersion.has(p.id);
              const owner = ownerSummary(p);
              return (
                <label key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 cursor-pointer hover:bg-[var(--color-bg-elevated)]">
                  <input type="checkbox" className="accent-[var(--color-primary)]" checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-mono text-[var(--color-text-primary)] truncate">
                      {p.short_number || p.teryt}
                    </span>
                    {/* Gmina obok TERYT-u — na jednym terenie bywają dwie różne gminy,
                        a wspólna mapa idzie do urzędu konkretnej gminy. */}
                    <span className="block text-[10px] font-mono text-[var(--color-text-muted)] truncate">
                      {p.teryt}
                      {(p.commune ?? "").trim() ? ` · gm. ${p.commune.trim()}` : ""}
                    </span>
                  </span>
                  {/* Kod własności (grupa rejestrowa) — kropka + kod, jak w dymku mapy. */}
                  <span
                    className="flex items-center gap-1 text-[10px] font-mono text-[var(--color-text-secondary)] whitespace-nowrap"
                    title={opt?.label ? `Grupa własności: ${opt.label}` : "Grupa własności (kod)"}
                  >
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: ownershipCodeColor(p.ownership_code) }} />
                    {p.ownership_code || "—"}
                  </span>
                  {/* Właściciel obok grupy własności — sam kod mówi tylko „osoba
                      fizyczna / gmina / Skarb Państwa", a przy wyborze działek do
                      wspólnej mapy trzeba widzieć, KTO nim jest (i czy to ten sam
                      właściciel, co na sąsiedniej działce). */}
                  <span
                    className="shrink-0 max-w-[11rem] truncate text-[10px] font-mono text-[var(--color-text-secondary)]"
                    title={owner.title}
                  >
                    {owner.label}
                  </span>
                  {/* Jednostka ewidencyjna obok kodu własności — to po niej dzieli się
                      wspólny zestaw formatek między urzędy. */}
                  <span
                    className="shrink-0 max-w-[10rem] truncate text-[10px] font-mono text-[var(--color-text-muted)]"
                    title={unit ? `Jednostka ewidencyjna: ${unit}` : "Brak jednostki ewidencyjnej"}
                  >
                    {unit || "—"}
                  </span>
                  {offCategory && (
                    <span
                      className="text-[9px] font-mono uppercase text-slate-500 bg-slate-100 border border-slate-200 rounded px-1 py-[1px] whitespace-nowrap"
                      title="Poza kategorią tego zestawu — dodana ręcznie przez wyszukanie"
                    >
                      {isPrivateParcel(p) ? "prywatna" : "urzędowa"}
                    </span>
                  )}
                  {offVersion && (
                    <span
                      className="text-[9px] font-mono uppercase text-amber-600 whitespace-nowrap"
                      title={`Brak trasy i obiektów na tej działce w wersji ${routeVersion.label} — zostanie pominięta`}
                    >
                      poza wariantem
                    </span>
                  )}
                  {!p.geometry && (
                    <span className="text-[9px] font-mono uppercase text-amber-600" title="Brak geometrii — zostanie pominięta">brak geom.</span>
                  )}
                </label>
              );
            })}
            {rows.length === 0 && (
              <p className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] text-center py-6">
                {anyFilter
                  ? "Brak działek pasujących do filtrów"
                  : q.trim()
                    ? "Brak działek pasujących do wyszukiwania"
                    : office
                      ? `Brak działek urzędowych na wersji ${routeVersion.label} — wpisz TERYT, aby dodać inną`
                      : `Brak działek prywatnych na wersji ${routeVersion.label} — wpisz TERYT, aby dodać inną`}
              </p>
            )}
          </div>
        )}

        {/* Ile zestawów powstanie i dla kogo — po jednym na urząd-właściciela. */}
        {office && officeGroups.length > 0 && (
          <div className="text-[11px] rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-2 py-1 space-y-0.5">
            <p className="text-[var(--color-text-secondary)]">
              {officeGroups.length === 1 ? (
                <>Powstanie <b>jeden</b> zestaw:</>
              ) : (
                <>Powstanie <b>{officeGroups.length}</b> osobnych zestawów (osobne wnioski):</>
              )}
            </p>
            <ul className="font-mono text-[var(--color-text-secondary)]">
              {officeGroups.map((g) => (
                <li key={g.key || "__none"} className="flex items-center gap-2">
                  <Building2 size={11} className="shrink-0 text-[var(--color-text-muted)]" />
                  <span className={clsx("truncate", !g.key && "italic text-amber-700")}>{g.name}</span>
                  <span className="ml-auto shrink-0 text-[var(--color-text-muted)]">
                    {g.parcelIds.length} dz.
                  </span>
                </li>
              ))}
            </ul>
            {officeGroups.some((g) => !g.key) && (
              <p className="text-[10px] text-amber-700">
                Działki bez urzędu-właściciela (brak wypisu albo właściciel bez urzędu) idą
                razem do jednego zestawu — uzupełnij właścicieli, jeśli to różne urzędy.
              </p>
            )}
          </div>
        )}

        {/* Dla jakiej gminy powstanie mapa — a przy kilku gminach naraz ostrzeżenie,
            bo wspólny zestaw urzędowy zwykle powinien obejmować jedną gminę. */}
        {office && sel.size > 0 && selCommunes.length > 0 && officeGroups.length <= 1 && (
          <p
            className={clsx(
              "text-[11px] rounded border px-2 py-1",
              selCommunes.length > 1
                ? "border-amber-300 bg-amber-50 text-amber-700"
                : "border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]",
            )}
          >
            {selCommunes.length > 1 ? (
              <>
                ⚠ Zaznaczone działki leżą w <b>{selCommunes.length} gminach</b>:{" "}
                <b>{selCommunes.join(", ")}</b> — mapa dla urzędu zwykle obejmuje jedną
                gminę; rozważ osobne zestawy.
              </>
            ) : (
              <>
                Mapa będzie generowana dla gminy: <b>{selCommunes[0]}</b>
              </>
            )}
          </p>
        )}
        <label className="flex items-center gap-2 cursor-pointer text-[11px] text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            className="accent-[var(--color-primary)]"
            checked={includeOrientation}
            onChange={(e) => setIncludeOrientation(e.target.checked)}
          />
          Arkusz orientacyjny — dodatkowy pierwszy arkusz z rozkładem (obrysami i
          numerami) pozostałych arkuszy
        </label>
        <div className="flex justify-end items-center gap-2 pt-1">
          <span className="mr-auto text-[11px] font-mono text-[var(--color-text-muted)]">Wybrano: {sel.size}</span>
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm"
            disabled={sel.size === 0 || loading}
            loading={loading}
            onClick={() => onConfirm([...sel], { includeOrientation, officeGroups })}
          >
            {office
              ? officeGroups.length > 1
                ? `Utwórz ${officeGroups.length} ${pluralZestawy(officeGroups.length)} (${sel.size} dz.)`
                : `Utwórz wspólny (${sel.size})`
              : `Utwórz osobne (${sel.size})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
